import type { Session } from './types'

/*
 * The signed-in session.
 *
 * A BEARER TOKEN HELD CLIENT-SIDE, matching BirdJournal.SupportSite. This is PROVISIONAL — design
 * `platform-architecture` §13.6 records it as chosen so the site was not blocked, and requires it to be
 * re-reviewed before this work is called done. The likely end state is a `Domain=birdjournal.com` cookie
 * giving single sign-on across the estate, with the token held server-side.
 *
 * Everything token-shaped is confined to this file so that switch is one module, not a search across the
 * site. Nothing else may read storage or touch the token.
 */

const KEY = 'bj.account.session'

/*
 * "Remember me" is the difference between the two storages, which is what the checkbox has always
 * actually meant — the Website's RememberMe made its OWIN cookie persistent. sessionStorage dies with
 * the tab, which is the right default on a shared machine for a page showing billing details.
 */
const stores = () => [localStorage, sessionStorage]

function load(): Session | null {
  for (const store of stores()) {
    const raw = store.getItem(KEY)
    if (!raw) continue
    try {
      const session = JSON.parse(raw) as Session
      // An expired token is not a session. Checking here means an expired tab shows the sign-in page
      // rather than a flash of account chrome followed by a 401.
      if (Date.parse(session.expiresAt) > Date.now()) return session
      store.removeItem(KEY)
    } catch {
      // Unreadable is indistinguishable from absent, and recovering is not worth a branch.
      store.removeItem(KEY)
    }
  }
  return null
}

let current = $state<Session | null>(load())

export const session = {
  get current() {
    return current
  },
  get signedIn() {
    return current !== null
  },
  get email() {
    return current?.account.email ?? null
  },
  get token() {
    return current?.token ?? null
  },

  remember(next: Session, persist: boolean) {
    // Clear BOTH first. Signing in without "remember me" after a remembered session would otherwise
    // leave the old token in localStorage, and load() prefers it — so the box would appear not to work.
    for (const store of stores()) store.removeItem(KEY)
    ;(persist ? localStorage : sessionStorage).setItem(KEY, JSON.stringify(next))
    current = next
  },

  clear() {
    for (const store of stores()) store.removeItem(KEY)
    current = null
  },
}
